import { useMemo, useState } from 'react';
import { Box, MenuItem, Table, TableBody, TableHead, TableRow, Tooltip } from '@mui/material';
import {
  ActionIconButton,
  CustomAutoComplete,
  CustomTextField,
  GeneralTableBox,
  HeadTableCell,
  HorizontalForm,
  NoDataSkeleton,
  PaginationTable,
  RowTableCell,
  TableBodySkeleton,
} from '../../../common/components';
import { useSupplierLedgerProviders } from '../../../hooks/useSupplierLedger';
import { compactFormSx, tabPanelSx } from '../constants/supplierLedgerUiConstants';
import { formatAmount } from '../utils/supplierLedgerFormatters';
import useClientPagination from '../hooks/useClientPagination';
import ProviderDetailDrawer from './ProviderDetailDrawer';

const ProvidersTab = () => {
  const [filters, setFilters] = useState({ provider: '', with_pending_balance: '' });
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const apiFilters = useMemo(
    () => Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== '')),
    [filters],
  );
  const { data: providers = [], isLoading } = useSupplierLedgerProviders(apiFilters);
  const visibleProviders = useMemo(() => {
    const hasAmount = (provider) => (
      Math.abs(Number(provider.total_invoiced_ars || 0)) > 0
      || Math.abs(Number(provider.paid_amount_ars || 0)) > 0
      || Math.abs(Number(provider.pending_amount_ars || 0)) > 0
      || Math.abs(Number(provider.ledger_balance_ars || 0)) > 0
    );

    const compareAmountDesc = (left, right, field) => (
      Number(right[field] || 0) - Number(left[field] || 0)
    );

    return providers
      .filter(hasAmount)
      .sort((left, right) => (
        compareAmountDesc(left, right, 'pending_amount_ars')
        || compareAmountDesc(left, right, 'ledger_balance_ars')
        || compareAmountDesc(left, right, 'total_invoiced_ars')
        || String(left.provider_name || '').localeCompare(String(right.provider_name || ''))
      ));
  }, [providers]);
  const providerPagination = useClientPagination(visibleProviders);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Box sx={tabPanelSx}>
      <HorizontalForm sx={compactFormSx}>
        <CustomTextField label="Proveedor o CUIT" name="provider" value={filters.provider} onChange={handleChange} sx={{ width: 320 }} />
        <CustomAutoComplete label="Saldo" name="with_pending_balance" value={filters.with_pending_balance} onChange={handleChange} sx={{ width: 220 }}>
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="true">Con saldo</MenuItem>
          <MenuItem value="false">Sin saldo</MenuItem>
        </CustomAutoComplete>
      </HorizontalForm>
      <GeneralTableBox>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <HeadTableCell>Proveedor</HeadTableCell>
              <HeadTableCell>CUIT</HeadTableCell>
              <HeadTableCell align="right" sx={{ textAlign: 'right' }}>Facturado ARS</HeadTableCell>
              <HeadTableCell align="right" sx={{ textAlign: 'right' }}>Pagado ARS</HeadTableCell>
              <HeadTableCell align="right" sx={{ textAlign: 'right' }}>Pendiente ARS</HeadTableCell>
              <HeadTableCell align="right" sx={{ textAlign: 'right' }}>Saldo ARS</HeadTableCell>
              <HeadTableCell>Acciones</HeadTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodySkeleton />
            ) : visibleProviders.length ? providerPagination.paginatedRows.map((provider) => (
              <TableRow key={provider.uuid_legal_entity} hover>
                <RowTableCell>{provider.provider_name}</RowTableCell>
                <RowTableCell>{provider.cuit || '-'}</RowTableCell>
                <RowTableCell align="right" sx={{ textAlign: 'right' }}>{formatAmount(provider.total_invoiced_ars)}</RowTableCell>
                <RowTableCell align="right" sx={{ textAlign: 'right' }}>{formatAmount(provider.paid_amount_ars)}</RowTableCell>
                <RowTableCell align="right" sx={{ textAlign: 'right' }}>{formatAmount(provider.pending_amount_ars)}</RowTableCell>
                <RowTableCell align="right" sx={{ textAlign: 'right' }}>{formatAmount(provider.ledger_balance_ars)}</RowTableCell>
                <RowTableCell>
                  <Tooltip title="Ver detalle">
                    <span>
                      <ActionIconButton variant="view" onClick={() => setSelectedProviderId(provider.uuid_legal_entity)} />
                    </span>
                  </Tooltip>
                </RowTableCell>
              </TableRow>
            )) : (
              <NoDataSkeleton colSpan={7} message="No hay proveedores con movimientos de cuenta corriente." />
            )}
          </TableBody>
        </Table>
      </GeneralTableBox>
      {!!visibleProviders.length && (
        <PaginationTable
          page={providerPagination.page}
          totalPages={providerPagination.totalPages}
          totalCount={providerPagination.totalCount}
          rowsPerPage={providerPagination.rowsPerPage}
          onPageChange={providerPagination.handlePageChange}
          onRowsPerPageChange={providerPagination.handleRowsPerPageChange}
        />
      )}
      <ProviderDetailDrawer providerId={selectedProviderId} onClose={() => setSelectedProviderId('')} />
    </Box>
  );
};

export default ProvidersTab;
